import React, { useRef, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  FileText,
  Upload,
  ChevronDown,
  ChevronUp,
  Trash2,
  ExternalLink,
  Loader2,
  Plus,
  AlertCircle,
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import api from '@/lib/api'
import {
  fetchCollaborationDocuments,
  uploadCollaborationDocument,
  deleteCollaborationDocument,
  selectCollaborationDocuments,
  selectDocumentsLoading,
  selectDocumentsUploading,
} from '@/redux/collaborationDocumentsSlice'

const LIME_SUBTLE_BUTTON_CLASS =
  'border-[#C0FD72]/40 bg-[#C0FD72]/10 text-[#3F7A0A] hover:bg-[#C0FD72]/20 hover:text-[#3F7A0A]'

interface CollaborationDocumentsProps {
  collaborationId: string
  isAccepted: boolean
}

export const CollaborationDocuments: React.FC<CollaborationDocumentsProps> = ({
  collaborationId,
  isAccepted,
}) => {
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = React.useState(true)
  const [deleteConfirm, setDeleteConfirm] = React.useState<string | null>(null)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  const [title, setTitle] = React.useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [openingDocId, setOpeningDocId] = useState<string | null>(null)

  const documents = useAppSelector((state) => selectCollaborationDocuments(state, collaborationId))
  const loading = useAppSelector((state) => selectDocumentsLoading(state, collaborationId))
  const uploading = useAppSelector((state) => selectDocumentsUploading(state, collaborationId))

  useEffect(() => {
    if (collaborationId && isAccepted) {
      dispatch(fetchCollaborationDocuments(collaborationId))
    }
  }, [collaborationId, isAccepted, dispatch])

  // Reset form when modal closes
  useEffect(() => {
    if (!isUploadModalOpen) {
      setTitle('')
      setSelectedFile(null)
      setError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [isUploadModalOpen])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setSelectedFile(file)
    setError(null)
  }

  const handleUpload = async () => {
    if (!title.trim()) {
      setError('Please enter a document title')
      return
    }

    if (title.length > 20) {
      setError('Title must be 20 characters or less')
      return
    }

    if (!selectedFile) {
      setError('Please select a PDF file')
      return
    }

    setError(null)
    const result = await dispatch(
      uploadCollaborationDocument({ collaborationId, file: selectedFile, title }),
    )

    if (uploadCollaborationDocument.fulfilled.match(result)) {
      setIsUploadModalOpen(false)
    }
  }

  const handleDelete = async (documentId: string) => {
    await dispatch(deleteCollaborationDocument({ collaborationId, documentId }))
    setDeleteConfirm(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleOpenDocument = async (docId: string) => {
    setOpeningDocId(docId)
    try {
      const response = await api.get(
        `/marketer/collaborations/${collaborationId}/documents/${docId}/download-url`,
      )
      window.open(response.data.downloadUrl, '_blank', 'noopener,noreferrer')
    } catch {
      // fall through silently
    } finally {
      setOpeningDocId(null)
    }
  }

  // Don't render if collaboration is not accepted
  if (!isAccepted) {
    return null
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-card border border-foreground/10 rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
        <CollapsibleTrigger className="w-full">
          <div className="flex flex-row items-center justify-between px-5 py-4">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-600" />
              <span className="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Contract Documents
              </span>
              {documents.length > 0 && (
                <span className="text-xs bg-muted text-gray-700 px-2 py-0.5 rounded-full">
                  {documents.length}
                </span>
              )}
            </div>
            {isOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 px-5 pb-5">
            {/* Add Document Modal */}
            <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
              <DialogTrigger asChild>
                <Button variant="lime" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Document
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                  <DialogDescription>Upload a contract or agreement (PDF only).</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Document Title</label>
                    <input
                      type="text"
                      placeholder="e.g. Signed Contract"
                      maxLength={20}
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value)
                        if (error) setError(null)
                      }}
                      className={`w-full px-3 py-2 text-sm border rounded-md bg-card text-foreground placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#C0FD72]/50 ${
                        error && !title ? 'border-red-500' : 'border-foreground/20'
                      }`}
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 text-right">{title.length}/20</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">File (PDF)</label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className={LIME_SUBTLE_BUTTON_CLASS}
                      >
                        Choose File
                      </Button>
                      <span className="text-sm text-gray-400 truncate">
                        {selectedFile ? selectedFile.name : 'No file selected'}
                      </span>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,application/pdf"
                      className="hidden"
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-600 text-sm rounded-md flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {error}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    variant="ghost"
                    onClick={() => setIsUploadModalOpen(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleUpload} disabled={uploading} variant="lime">
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      'Upload'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Loading state */}
            {loading && (
              <div className="text-center py-4 text-gray-600">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                Loading documents...
              </div>
            )}

            {/* Documents list */}
            {!loading && documents.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No documents uploaded yet.</p>
            )}

            {!loading && documents.length > 0 && (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-card border border-foreground/10 rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-5 w-5 text-red-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {doc.title || doc.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(doc.uploadedAt)}
                          {doc.uploadedBy && ` • ${doc.uploadedBy.name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-600 hover:text-foreground"
                        onClick={() => handleOpenDocument(doc.id)}
                        disabled={openingDocId === doc.id}
                      >
                        {openingDocId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                      </Button>
                      {deleteConfirm === doc.id ? (
                        <div className="flex gap-1">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                          >
                            Confirm
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-600 hover:text-foreground"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(doc.id)}
                          className="text-gray-600 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
